import { useState, useRef, useEffect } from "react";
import Input from "./Input";
import CartInfo from "components/Customize/CartInfo";
import {
  formatPhoneNumber,
  formatDateOfBirth,
  formatSSN,
  formatDateToYYYYMMDD,
  formatDateToMMDDYYYY,
  formatZipCode,
  removeParenthesisAndDashes,
  removeAlphaCharacters,
  useWrapRequest,
  dateOfBirthValidator,
  isValidEmail,
  formatEmailAddress,
} from "utils";
import { Accordion, Card, useAccordionButton, Modal, Alert } from "react-bootstrap";
import { states } from "utils/const";
import { getCheckoutPage } from "services/get-getCheckoutPage";
import { useNavigate } from "react-router-dom";
import { getOtherAuthUserTypes } from "services/get-getOtherAuthUserTypes";
import { CheckoutFormModel } from "atoms/models";
import { postCheckoutPage } from "services/post-checkoutPage";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { toastState, overlayState, cartState, mobileRegistryModalState, showChangeBillingAddressModalState } from "atoms";
import BillingAddressChangeModal from "./BillingAddressChangeModal";

interface ContextAwareToggleProps {
  children: React.ReactNode;
  eventKey: string;
  callback?: (eventKey: string) => void;
}

function ContextAwareToggle({ children, eventKey, callback }: ContextAwareToggleProps) {
  const decoratedOnClick = useAccordionButton(eventKey, () => callback && callback(eventKey));
  return <span onClick={decoratedOnClick}>{children}</span>;
}

const CheckoutForms = () => {
  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRef3 = useRef<HTMLInputElement>(null);
  const inputRef4 = useRef<HTMLInputElement>(null);
  const [cart, setCart] = useRecoilState(cartState);
  const setOverlay = useSetRecoilState(overlayState);
  const [toast, setToast] = useRecoilState(toastState);
  const navigate = useNavigate();
  const wrapRequest = useWrapRequest();
  const [iconClass, setIconClass] = useState(true);
  const [authTypes, setAuthTypes] = useState([]);
  const [showMobileRegistryModal, setShowMobileRegistryModal] = useRecoilState(mobileRegistryModalState);
  const [activeAccordionAuthUser, setActiveAccordionAuthUser] = useState("");
  const showChangeBillingAddressModal = useRecoilValue(showChangeBillingAddressModalState);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
  });
  const [checkoutForm, setCheckoutForm] = useState<CheckoutFormModel>({
    customerNameSuffix: "",
    customerNameFirst: "",
    customerNameLast: "",
    dateOfBirth: "",
    emailAddress: "",
    phoneNumber: "",
    customerSSN: "",
    creditCheckBypass: null,
    useMaxSecurityDeposit: true,
    otherAuthNamePrefix: "",
    otherAuthNameFirst: "",
    otherAuthNameLast: "",
    otherAuthType: "",
    otherAuthSSNLast4: "",
    billingStreetNumber: "",
    billingStreetName: "",
    billingPOBox: "",
    billingApartmentNumber: "",
    billingCity: "",
    billingState: "SC",
    billingZipCode: "",
  });

  const handleAccordionToggle = (panelKey: any) => {
    setActiveAccordionAuthUser(panelKey === activeAccordionAuthUser ? null : panelKey);
  };

  useEffect(() => {
    cart.otherAuthNameFirst && cart.otherAuthNameLast ? setActiveAccordionAuthUser("0") : setActiveAccordionAuthUser("");
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchCheckoutPage = () => {
      wrapRequest(async () => {
        const getCheckoutPageResponse = await getCheckoutPage();
        const otherAuthUserTypes = await getOtherAuthUserTypes();
        const links = getCheckoutPageResponse["_links"];
        const windowLocation = "ec:windowLocation";

        if (getCheckoutPageResponse._embedded) {
          const cartResponse = getCheckoutPageResponse._embedded["ec:cart"];
          setCart(cartResponse);
          setAuthTypes(otherAuthUserTypes);
          setCheckoutForm({
            customerNameSuffix: cartResponse.customerNameSuffix,
            customerNameFirst: cartResponse.customerNameFirst,
            customerNameLast: cartResponse.customerNameLast,
            dateOfBirth: cartResponse.dateOfBirth ? formatDateToMMDDYYYY(cartResponse.dateOfBirth) : cartResponse.dateOfBirth,
            emailAddress: cartResponse.emailAddress,
            phoneNumber: cartResponse.phoneNumber ? formatPhoneNumber(cartResponse.phoneNumber) : cartResponse.phoneNumber,
            customerSSN: cartResponse.customerSSN ? formatSSN(cartResponse.customerSSN) : cartResponse.customerSSN,
            creditCheckBypass: cartResponse.creditCheckBypass,
            useMaxSecurityDeposit: cartResponse.useMaxSecurityDeposit,
            otherAuthNamePrefix: cartResponse.otherAuthNamePrefix,
            otherAuthNameFirst: cartResponse.otherAuthNameFirst,
            otherAuthNameLast: cartResponse.otherAuthNameLast,
            otherAuthType: cartResponse.otherAuthType,
            otherAuthSSNLast4: "",
            billingStreetNumber: cartResponse.billingPOBox ? " " : cartResponse.billingStreetNumber,
            billingStreetName: cartResponse.billingPOBox ? " " : cartResponse.billingStreetName,
            billingPOBox: cartResponse.billingPOBox,
            billingApartmentNumber: cartResponse.billingApartmentNumber,
            billingCity: cartResponse.billingCity,
            billingState: cartResponse.billingState,
            billingZipCode: cartResponse.billingZipCode,
          });
        } else {
          navigate(links[windowLocation].href);
        }
      });
    };
    fetchCheckoutPage();
    //eslint-disable-next-line
  }, [navigate, setCheckoutForm, setCart]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let formattedValue: any;
    if (name === "phoneNumber") {
      formattedValue = formatPhoneNumber(value);
    } else if (name === "dateOfBirth") {
      formattedValue = formatDateOfBirth(value);
    } else if (name === "customerSSN") {
      formattedValue = formatSSN(value);
    } else if (name === "billingZipCode") {
      formattedValue = formatZipCode(value);
    } else if (name === "otherAuthSSNLast4") {
      formattedValue = removeAlphaCharacters(value);
    } else if (name === "billingStreetNumber") {
      formattedValue = removeAlphaCharacters(value);
    } else if (name === "emailAddress") {
      formattedValue = formatEmailAddress(value);
    } else {
      formattedValue = value;
    }

    setCheckoutForm((prevState) => ({
      ...prevState,
      [name]: formattedValue,
    }));
  };

  const focusNextInput = () => {
    // Create an array of current refs
    const refsArray = [inputRef1, inputRef2, inputRef3, inputRef4];
    // Find the index of the current input ref
    const currentIndex = refsArray.findIndex((ref) => ref.current === document.activeElement);
    // If currentIndex is -1 (not found) or the current input is the last one, do nothing
    if (currentIndex === -1 || currentIndex === refsArray.length - 1) {
      return;
    }
    // Otherwise, focus on the next input ref
    refsArray[currentIndex + 1].current?.focus();
  };

  const handleSubmit = async (e?: any) => {
    if (e) {
      e.preventDefault();
    }
    if (showChangeBillingAddressModal) {
      return;
    }

    //Strip out dashes, spaces and parenthesis
    const modifiedCheckoutForValues = {
      ...checkoutForm,
      dateOfBirth: formatDateToYYYYMMDD(checkoutForm.dateOfBirth),
      phoneNumber: removeParenthesisAndDashes(checkoutForm.phoneNumber),
      customerSSN: checkoutForm.customerSSN ? removeParenthesisAndDashes(checkoutForm.customerSSN) : checkoutForm.customerSSN,
    };

    wrapRequest(async () => {
      const postCheckoutPageResponse = await postCheckoutPage(modifiedCheckoutForValues);
      const errorMessageObject = {
        errorMessage: postCheckoutPageResponse.message,
        errorMessageType: postCheckoutPageResponse.ecommerceMessageType,
      };
      const links = postCheckoutPageResponse["_links"];
      const windowLocation = "ec:windowLocation";

      if (links && links[windowLocation] && links[windowLocation].href) {
        navigate(links[windowLocation].href);
        setOverlay(false);
      } else if (postCheckoutPageResponse && postCheckoutPageResponse.ecommerceMessageType === "banner") {
        setOverlay(false);
        setAlert({ ...alert, show: true, message: errorMessageObject.errorMessage });
      } else {
        setOverlay(false);
        setToast({
          ...toast,
          show: true,
          variant: "danger",
          header: "Error",
          message: `${postCheckoutPageResponse.message}`,
        });
      }
    });
  };

  const convertFirstCharacterToCapital = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const disableContinueButton =
    !checkoutForm.customerNameFirst ||
    checkoutForm.customerNameFirst?.length < 2 ||
    !checkoutForm.customerNameLast ||
    checkoutForm.customerNameLast?.length < 2 ||
    !checkoutForm.phoneNumber ||
    checkoutForm.phoneNumber?.length !== 14 ||
    !checkoutForm.dateOfBirth ||
    !dateOfBirthValidator(checkoutForm.dateOfBirth) ||
    checkoutForm.dateOfBirth?.length !== 10 ||
    !checkoutForm.customerSSN ||
    checkoutForm.customerSSN?.length !== 11 ||
    !checkoutForm.emailAddress ||
    !isValidEmail(checkoutForm.emailAddress) ||
    !checkoutForm.billingStreetNumber ||
    !checkoutForm.billingStreetName ||
    !checkoutForm.billingCity ||
    !checkoutForm.billingState ||
    !(checkoutForm.billingZipCode.length === 5 || checkoutForm.billingZipCode.length === 10);

  const disableContinueButtonForExistingCustomer =
    !checkoutForm.customerNameFirst ||
    checkoutForm.customerNameFirst?.length < 2 ||
    !checkoutForm.customerNameLast ||
    checkoutForm.customerNameLast?.length < 2 ||
    !checkoutForm.phoneNumber ||
    checkoutForm.phoneNumber?.length !== 14 ||
    !checkoutForm.dateOfBirth ||
    !dateOfBirthValidator(checkoutForm.dateOfBirth) ||
    checkoutForm.dateOfBirth?.length !== 10 ||
    (!checkoutForm.customerSSN && checkoutForm.customerSSN?.length !== 11 && !cart.creditCheckBypass) ||
    !checkoutForm.emailAddress ||
    !isValidEmail(checkoutForm.emailAddress);

  return (
    <div className="checkout-forms-container">
      <div className="d-flex">
        <form onSubmit={handleSubmit} className="me-3">
          <div className="page-text-header mb-5 mt-5">
            <strong>Please enter your information below.</strong>
            <div> We will not share or sell your personal information. Fields in red are required.</div>
          </div>
          {alert.show ? (
            <Alert variant="danger" onClose={() => setAlert({ ...alert, show: false })} dismissible>
              <Alert.Heading>There was an error processing your information.</Alert.Heading>
              <p>
                Please verify name, date of birth and SSN then resubmit. If you continue to have issues, please call us at{" "}
                <a href="tel:1-888-403-2667\">1-888-403-2667</a>.
              </p>
            </Alert>
          ) : null}
          <div className="primary-account-header">Primary Account Holder</div>
          <div className="row mt-3">
            <div className="col-md-5 col-sm-12 mb-3">
              <Input
                label="First Name"
                name="customerNameFirst"
                value={checkoutForm.customerNameFirst ? convertFirstCharacterToCapital(checkoutForm.customerNameFirst) : ""}
                onChange={handleInputChange}
                type="text"
                required={true}
                errorMessage="First Name should be at least 2 characters."
                maxLength={34}
                autoComplete="given-name"
                disabled={cart.isCurrentCustomer ? true : false}
              />
            </div>
            <div className="col-md-5 col-sm-12 mb-3">
              <Input
                label="Last Name"
                name="customerNameLast"
                value={checkoutForm.customerNameLast ? convertFirstCharacterToCapital(checkoutForm.customerNameLast) : ""}
                onChange={handleInputChange}
                type="text"
                required={true}
                errorMessage="Last Name should be at least 2 characters."
                maxLength={30}
                autoComplete="family-name"
                disabled={cart.isCurrentCustomer ? true : false}
              />
            </div>
            <div className="form-floating col-md-2 mb-3">
              <select
                className={!checkoutForm.customerNameSuffix ? "form-select" : "form-select is-valid"}
                id="floatingSelect"
                value={checkoutForm.customerNameSuffix ? checkoutForm.customerNameSuffix : ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCheckoutForm({ ...checkoutForm, customerNameSuffix: e.target.value })}
                disabled={cart.isCurrentCustomer}
              >
                <option value=""></option>
                <option value="Jr.">Jr.</option>
                <option value="Sr.">Sr.</option>
                <option value="I">I</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
              </select>
              <label htmlFor="floatingSelect" style={{ marginLeft: "10px" }}>
                Suffix
              </label>
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-12 mb-3">
              <Input
                label="Phone Number"
                name="phoneNumber"
                value={checkoutForm.phoneNumber ? checkoutForm.phoneNumber : ""}
                onChange={handleInputChange}
                type="tel"
                required={true}
                errorMessage="Invalid Phone Number. Please enter a valid 10 digit number."
                maxLength={14}
                inputRef={inputRef1}
                focusNext={focusNextInput}
                disabled={cart.isCurrentCustomer && cart.phoneNumber?.length > 0 ? true : false}
                inputMode="numeric"
              />
            </div>
            <div className="col-md-4 col-sm-12 mb-3">
              <Input
                label={window.innerWidth < 991 && window.innerWidth > 400 ? "Date Of Birth" : "DOB (MM-DD-YYYY)"}
                name="dateOfBirth"
                value={checkoutForm.dateOfBirth ? checkoutForm.dateOfBirth : ""}
                onChange={handleInputChange}
                type="text"
                required={true}
                errorMessage="Invalid birth date."
                maxLength={10}
                inputRef={inputRef2}
                focusNext={focusNextInput}
                disabled={cart.isCurrentCustomer && cart.dateOfBirth?.length > 0 ? true : false}
                inputMode="numeric"
              />
            </div>
            <div className="col-md-4 col-sm-12 mb-3">
              <Input
                label={window.innerWidth < 991 && window.innerWidth > 400 ? "SSN" : "Social Security Number"}
                name="customerSSN"
                value={checkoutForm.customerSSN ? checkoutForm.customerSSN : ""}
                onChange={handleInputChange}
                type="text"
                required={true}
                errorMessage="Please enter full SSN."
                maxLength={11}
                inputRef={inputRef3}
                focusNext={focusNextInput}
                disabled={cart.creditCheckBypass}
                inputMode="numeric"
              />
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-12 mb-3">
              <Input
                label="Email Address"
                name="emailAddress"
                value={checkoutForm.emailAddress ? checkoutForm.emailAddress : ""}
                onChange={handleInputChange}
                type="email"
                required={true}
                errorMessage="Invalid Email Address. Please enter a valid Email Address."
                inputRef={inputRef4}
                focusNext={focusNextInput}
                disabled={cart.isCurrentCustomer && cart.emailAddress?.length > 0 ? true : false}
              />
            </div>
          </div>
          {!cart?.isCurrentCustomer ? (
            <div className="row mt-5">
              <div className="bill-notification-blurb">
                <strong>*Bill notifications &amp; cart reminders will be sent to this email address.</strong>
              </div>
              <Accordion activeKey={activeAccordionAuthUser} onSelect={handleAccordionToggle}>
                <Card>
                  <Card.Header>
                    <div className="row mt-2 mb-2 flex-grow-1">
                      <div className="add-account-text d-flex justify-content-start align-items-center">
                        <i className="fa-solid fa-circle-user me-2"></i>
                        <div className="flex-grow-1">
                          {checkoutForm.otherAuthNameFirst && checkoutForm.otherAuthNameLast ? (
                            <h4 style={{ color: "#f2a900", marginBottom: "-1px" }}>
                              {checkoutForm.otherAuthNameFirst} {checkoutForm.otherAuthNameLast}
                            </h4>
                          ) : (
                            <>
                              <div className="add-account-text-heading">Add Authorized User</div>
                              <div className="add-account-text-body">
                                Give authority to a family member or friend to make changes to your account.
                              </div>
                            </>
                          )}
                        </div>
                        <ContextAwareToggle eventKey="0">
                          <i
                            className={iconClass ? "fa-solid fa-circle-plus" : "fa-solid fa-circle-minus"}
                            onClick={() => setIconClass(!iconClass)}
                          ></i>
                        </ContextAwareToggle>
                      </div>
                    </div>
                  </Card.Header>
                  <Accordion.Collapse eventKey="0">
                    <Card.Body>
                      <div className="row">
                        <div className="col-md-6 col-sm-12 mb-3">
                          <Input
                            label="First Name"
                            name="otherAuthNameFirst"
                            value={checkoutForm.otherAuthNameFirst ? checkoutForm.otherAuthNameFirst : ""}
                            onChange={handleInputChange}
                            type="text"
                            required={activeAccordionAuthUser === "0" ? true : false}
                            errorMessage="Name should be at least 2 characters."
                            maxLength={10}
                            autoComplete="given-name"
                          />
                        </div>
                        <div className="col-md-6 col-sm-12 mb-3">
                          <Input
                            label="Last Name"
                            name="otherAuthNameLast"
                            value={checkoutForm.otherAuthNameLast ? checkoutForm.otherAuthNameLast : ""}
                            onChange={handleInputChange}
                            type="text"
                            required={activeAccordionAuthUser === "0" ? true : false}
                            errorMessage="Name should be at least 2 characters."
                            maxLength={10}
                          />
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="form-floating col-md-6 col-sm-12 mb-3">
                          <select
                            className={!checkoutForm.otherAuthType ? "form-select is-invalid" : "form-select is-valid"}
                            id="floatingSelect"
                            required={activeAccordionAuthUser === "0" ? true : false}
                            value={checkoutForm.otherAuthType ? checkoutForm.otherAuthType : ""}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              setCheckoutForm({ ...checkoutForm, otherAuthType: e.target.value })
                            }
                          >
                            {authTypes.length === 0
                              ? null
                              : authTypes.map((type: any) => {
                                  return (
                                    <option key={type} value={type}>
                                      {type}
                                    </option>
                                  );
                                })}
                          </select>
                          <label htmlFor="floatingSelect" className="ms-2">
                            Relationship to Primary
                          </label>
                        </div>
                        <div className="col-md-6 col-sm-12 mb-3">
                          <Input
                            label="SSN - Last Four Numbers"
                            name="otherAuthSSNLast4"
                            value={checkoutForm.otherAuthSSNLast4 ? checkoutForm.otherAuthSSNLast4 : ""}
                            onChange={handleInputChange}
                            type="text"
                            required={activeAccordionAuthUser === "0" ? true : false}
                            errorMessage="Must be last 4 numbers of social security number."
                            maxLength={4}
                            inputMode="numeric"
                          />
                        </div>
                        <div className="d-flex justify-content-end mt-3">
                          <ContextAwareToggle eventKey="0">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setCheckoutForm({
                                  ...checkoutForm,
                                  otherAuthNameFirst: "",
                                  otherAuthSSNLast4: "",
                                  otherAuthNameLast: "",
                                  otherAuthType: "",
                                });
                                setIconClass(!iconClass);
                              }}
                            >
                              Cancel
                            </button>
                          </ContextAwareToggle>
                        </div>
                      </div>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            </div>
          ) : null}
          {!cart?.isCurrentCustomer ? (
            <>
              <div className="row">
                <div className="primary-account-header mt-5">Comporium Billing Address</div>
                <BillingAddressChangeModal setCheckoutForm={setCheckoutForm} checkoutForm={checkoutForm} buttonTitle="Change Billing Address" />
                <div className="row">
                  <div className="col-md-6 col-sm-12 mb-3">
                    <Input
                      label="Street Number"
                      name="billingStreetNumber"
                      value={checkoutForm.billingStreetNumber ? checkoutForm.billingStreetNumber : "Change Billing Address"}
                      onChange={handleInputChange}
                      type="text"
                      required={true}
                      maxLength={8}
                      autoComplete="disable-auto-complete"
                      disabled={true}
                      inputMode="numeric"
                    />
                  </div>
                  <div className="col-md-6 col-sm-12 mb-3">
                    <Input
                      label="Street Name"
                      name="billingStreetName"
                      value={checkoutForm.billingStreetName ? checkoutForm.billingStreetName : ""}
                      onChange={handleInputChange}
                      type="text"
                      required={true}
                      maxLength={22}
                      autoComplete="disable-auto-complete"
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 col-sm-12 mb-3">
                  <Input
                    label="Apartment #"
                    name="billingApartmentNumber"
                    value={checkoutForm.billingApartmentNumber ? checkoutForm.billingApartmentNumber : ""}
                    onChange={handleInputChange}
                    type="text"
                    required={false}
                    maxLength={5}
                    autoComplete="disable-auto-complete"
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-3">
                  <Input
                    label="P.O. Box"
                    name="billingPOBox"
                    value={checkoutForm.billingPOBox ? checkoutForm.billingPOBox : ""}
                    onChange={handleInputChange}
                    type="text"
                    required={false}
                    maxLength={6}
                    autoComplete="disable-auto-complete"
                    disabled={true}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 col-sm-12 mb-3">
                  <Input
                    label="City"
                    name="billingCity"
                    value={checkoutForm.billingCity ? checkoutForm.billingCity : ""}
                    onChange={handleInputChange}
                    type="text"
                    required={true}
                    autoComplete="disable-auto-complete"
                    disabled={true}
                  />
                </div>
                <div className="form-floating col-md-4 col-sm-12 mb-3">
                  <select
                    className={!checkoutForm.billingState ? "form-select is-invalid" : "form-select is-valid"}
                    id="floatingSelect"
                    required={iconClass ? false : true}
                    value={checkoutForm.billingState ? checkoutForm.billingState : ""}
                    autoComplete="disable-auto-complete"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCheckoutForm({ ...checkoutForm, billingState: e.target.value })}
                    disabled={true}
                  >
                    {!states
                      ? null
                      : states.map((state: { abbreviation: string; name: string }) => {
                          return (
                            <option key={state.abbreviation} value={state.abbreviation}>
                              {state.name}
                            </option>
                          );
                        })}
                  </select>
                  <label htmlFor="floatingSelect" className="ms-2">
                    State
                  </label>
                </div>
                <div className="col-md-4 col-sm-12 mb-3">
                  <Input
                    label="Zip Code"
                    name="billingZipCode"
                    value={checkoutForm.billingZipCode ? checkoutForm.billingZipCode : ""}
                    onChange={handleInputChange}
                    type="text"
                    required={true}
                    errorMessage="Invalid ZIP. Please enter a valid ZIP Number."
                    maxLength={10}
                    autoComplete="disable-auto-complete"
                    disabled={true}
                    inputMode="numeric"
                  />
                </div>
              </div>
            </>
          ) : null}
          {!cart?.creditCheckBypass ? (
            <>
              <div className="row mt-4 mb-4">
                <div className="credit-check-info-body">
                  After selecting Continue, we will run a secure, soft credit inquiry through TransUnion in order to waive most security deposits.
                  This inquiry will NOT affect your credit score.
                </div>
              </div>
              <div className="row mt-4 mb-4">
                <div className="form-check credit-check">
                  <input
                    className="form-check-input ms-1 me-1"
                    id="creditCheck"
                    type="checkbox"
                    checked={checkoutForm.useMaxSecurityDeposit}
                    onChange={() => setCheckoutForm({ ...checkoutForm, useMaxSecurityDeposit: !checkoutForm.useMaxSecurityDeposit })}
                  />
                  <label className="form-check-label" htmlFor="creditCheck">
                    Don’t process my credit. I’ll pay the maximum security deposit(s) for my service(s).
                  </label>
                </div>
              </div>
            </>
          ) : null}

          <hr />
          <div className="row btn-continue-container">
            <button
              type="submit"
              className="btn btn-default btn-continue mb-5"
              disabled={cart?.isCurrentCustomer ? disableContinueButtonForExistingCustomer : disableContinueButton}
              onClick={() => window.scrollTo(0, 0)}
            >
              Continue
            </button>
          </div>
        </form>
        <div className="cart-registry">
          {Object.keys(cart).length > 1 ? (
            <CartInfo
              cart={cart}
              disableContinueButton={cart?.isCurrentCustomer ? disableContinueButtonForExistingCustomer : disableContinueButton}
              onContinue={handleSubmit}
            />
          ) : null}
        </div>
        <Modal show={showMobileRegistryModal} onHide={() => setShowMobileRegistryModal(false)}>
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body>{Object.keys(cart).length > 1 ? <CartInfo cart={cart} onContinue={handleSubmit} /> : null}</Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default CheckoutForms;
